import { useList, useCustomMutation, useGo } from '@refinedev/core';
import { useState, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  User,
  Calendar,
  FileText,
  MessageCircle,
  Star,
  Image as ImageIcon,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DataTable, type Column, type SortState, type BulkAction } from '@/components/tables';
import DOMPurify from 'dompurify';
import type { ModerationQueueItem, ModeratableType, ModerationStatus } from '../types';

function getTypeIcon(type: ModeratableType) {
  switch (type) {
    case 'POST':
      return <FileText className="h-4 w-4" />;
    case 'COMMENT':
      return <MessageCircle className="h-4 w-4" />;
    case 'REVIEW':
      return <Star className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
}

function getTypeLabel(type: ModeratableType): string {
  switch (type) {
    case 'POST':
      return 'Пост';
    case 'COMMENT':
      return 'Комментарий';
    case 'REVIEW':
      return 'Отзыв';
    default:
      return type;
  }
}

/** Нормализует статус к UPPERCASE для маппинга */
function normalizeStatus(status: string): ModerationStatus {
  return status.toUpperCase() as ModerationStatus;
}

function getStatusIcon(status: ModerationStatus) {
  switch (normalizeStatus(status)) {
    case 'PENDING':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'APPROVED':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'REJECTED':
      return <XCircle className="h-4 w-4 text-destructive" />;
    default:
      return null;
  }
}

const STATUS_LABELS: Record<ModerationStatus, string> = {
  PENDING: 'На модерации',
  APPROVED: 'Одобрено',
  REJECTED: 'Отклонено',
};

const STATUS_COLORS: Record<ModerationStatus, 'default' | 'secondary' | 'destructive'> = {
  PENDING: 'secondary',
  APPROVED: 'default',
  REJECTED: 'destructive',
};

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getAuthorName(content: ModerationQueueItem['content']): string {
  if (!content?.author) {return 'Неизвестный';}
  const author = content.author;
  const name = [author.firstName, author.lastName].filter(Boolean).join(' ');
  if (name) {return name;}
  if (author.username) {return `@${author.username}`;}
  return `Пользователь #${author.id}`;
}

function formatRelativeTime(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) {return 'только что';}
  if (diffMin < 60) {return `${diffMin} мин.`;}
  if (diffHr < 24) {return `${diffHr} ч.`;}
  if (diffDay < 7) {return `${diffDay} дн.`;}
  return formatDate(dateString);
}

/**
 * Карусель изображений — копия PostImageCarousel из platform (без framer-motion)
 */
function ImageCarousel({ images }: { images: string[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (images.length === 0) {return null;}

  const hasMultiple = images.length > 1;

  return (
    <div className="relative aspect-square overflow-hidden bg-muted">
      <img
        src={images[currentIndex]}
        alt=""
        className="size-full object-cover"
      />

      {hasMultiple && (
        <>
          {/* Counter Badge */}
          <div className="absolute right-3 top-3 rounded-full bg-black/70 px-2.5 py-1 text-xs font-semibold text-white">
            {currentIndex + 1}/{images.length}
          </div>

          {/* Prev Arrow */}
          {currentIndex > 0 && (
            <button
              type="button"
              onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
              className="absolute left-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md transition-transform hover:scale-105"
            >
              <ChevronLeft className="size-4 text-black" />
            </button>
          )}

          {/* Next Arrow */}
          {currentIndex < images.length - 1 && (
            <button
              type="button"
              onClick={() => setCurrentIndex((i) => Math.min(images.length - 1, i + 1))}
              className="absolute right-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md transition-transform hover:scale-105"
            >
              <ChevronRight className="size-4 text-black" />
            </button>
          )}

          {/* Dots */}
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1">
            {images.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setCurrentIndex(index)}
                className={`size-1.5 rounded-full transition-all ${
                  index === currentIndex
                    ? 'scale-100 bg-white'
                    : 'scale-75 bg-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Preview поста — точная копия PostCard из apps/platform,
 * адаптированная для admin (без Next.js зависимостей)
 */
function PostPreviewCard({ item }: { item: ModerationQueueItem }) {
  const authorName = getAuthorName(item.content);

  return (
    <article className="rounded-xl border overflow-hidden bg-card">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex size-10 items-center justify-center rounded-full bg-accent/20 shrink-0">
          <User className="size-5 text-muted-foreground" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold">{authorName}</span>
          <span className="text-xs text-muted-foreground">{formatRelativeTime(item.submittedAt)}</span>
        </div>

        <Badge variant="outline" className="gap-1 shrink-0">
          {getTypeIcon(item.entityType)}
          {getTypeLabel(item.entityType)}
        </Badge>
      </div>

      {/* Images — карусель как на платформе */}
      {item.content?.images && item.content.images.length > 0 && (
        <ImageCarousel images={item.content.images} />
      )}

      {/* Content */}
      {item.content?.text && (
        <div className="px-4 pb-3">
          <span className="text-sm font-semibold">{authorName}</span>
          <div
            className="text-sm leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_blockquote]:border-l-4 [&_blockquote]:border-muted-foreground/30 [&_blockquote]:pl-4 [&_blockquote]:italic [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-medium [&_h3]:mb-2 [&_a]:text-primary [&_a]:underline [&_strong]:font-semibold [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded [&_code]:text-sm"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content.text) }}
          />
        </div>
      )}

      {/* Рейтинг (для отзывов) */}
      {item.entityType === 'REVIEW' && item.content?.rating && (
        <div className="flex gap-0.5 px-4 pb-3">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`h-4 w-4 ${
                star <= item.content!.rating!
                  ? 'text-yellow-500 fill-yellow-500'
                  : 'text-muted-foreground'
              }`}
            />
          ))}
        </div>
      )}

      {/* Views */}
      <div className="flex items-center gap-1 px-4 pb-3">
        <Eye className="size-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{formatDate(item.submittedAt)}</span>
      </div>
    </article>
  );
}

function ContentPreviewSheet({
  item,
  open,
  onClose,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}: {
  item: ModerationQueueItem | null;
  open: boolean;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  isApproving: boolean;
  isRejecting: boolean;
}) {
  const go = useGo();

  if (!item) {return null;}

  const isPending = item.status === 'PENDING';

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[550px] sm:max-w-[550px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {getTypeIcon(item.entityType)}
            {getTypeLabel(item.entityType)} #{item.id}
          </SheetTitle>
          <SheetDescription>
            Отправлено на модерацию {formatDate(item.submittedAt)}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-200px)]">
          <div className="space-y-4 pr-4">
            {/* Статус */}
            <div className="flex items-center gap-2">
              {getStatusIcon(item.status)}
              <Badge variant={STATUS_COLORS[normalizeStatus(item.status)]}>
                {STATUS_LABELS[normalizeStatus(item.status)]}
              </Badge>
              {item.rejectionReason && (
                <span className="text-sm text-destructive ml-2">
                  — {item.rejectionReason}
                </span>
              )}
            </div>

            {/* Post Preview — как на платформе */}
            <PostPreviewCard item={item} />

            {/* Мета-информация */}
            <Card>
              <CardContent className="pt-4 space-y-2">
                {item.content?.author && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Автор</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0"
                      onClick={() => go({ to: `/users/show/${item.content!.author!.id}` })}
                    >
                      {getAuthorName(item.content)}
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Создано</span>
                  <span>{formatDate(item.submittedAt)}</span>
                </div>
                {item.reviewedAt && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Рассмотрено</span>
                    <span>{formatDate(item.reviewedAt)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>

        {/* Кнопки действий */}
        {isPending && (
          <div className="flex gap-2 mt-4 px-4 pb-4">
            <Button
              variant="default"
              className="flex-1"
              onClick={onApprove}
              disabled={isApproving || isRejecting}
            >
              {isApproving ? (
                'Одобрение...'
              ) : (
                <>
                  <ThumbsUp className="h-4 w-4 mr-2" />
                  Одобрить
                </>
              )}
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={onReject}
              disabled={isApproving || isRejecting}
            >
              {isRejecting ? (
                'Отклонение...'
              ) : (
                <>
                  <ThumbsDown className="h-4 w-4 mr-2" />
                  Отклонить
                </>
              )}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function RejectDialog({
  open,
  onClose,
  onConfirm,
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}) {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    if (!reason.trim()) {return;}
    onConfirm(reason);
    setReason('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Отклонить контент</DialogTitle>
          <DialogDescription>
            Укажите причину отклонения. Автор увидит эту причину.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="content-reject-reason">Причина отклонения *</Label>
          <Textarea
            id="content-reject-reason"
            placeholder="Например: Контент нарушает правила сообщества..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-2"
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isLoading || !reason.trim()}
          >
            {isLoading ? 'Отклонение...' : 'Отклонить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function getActiveFiltersCount(statusFilter: string, typeFilter: string): number {
  let count = 0;
  if (statusFilter !== 'all') {count++;}
  if (typeFilter !== 'all') {count++;}
  return count;
}

export function ContentModerationTab() {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState | undefined>({
    field: 'submittedAt',
    order: 'desc',
  });
  const [statusFilter, setStatusFilter] = useState<string>('PENDING');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedItem, setSelectedItem] = useState<ModerationQueueItem | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const pageSize = 20;

  const activeFiltersCount = getActiveFiltersCount(statusFilter, typeFilter);

  const queryFilters = useMemo(() => {
    const result = [];
    if (statusFilter !== 'all') {
      result.push({ field: 'status', operator: 'eq' as const, value: statusFilter });
    }
    if (typeFilter !== 'all') {
      result.push({ field: 'entityType', operator: 'eq' as const, value: typeFilter });
    }
    return result;
  }, [statusFilter, typeFilter]);

  const listResult = useList<ModerationQueueItem>({
    resource: 'moderation',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result.data ?? [];
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;
  const refetchList = listResult.query.refetch;

  const approveMutation = useCustomMutation();
  const rejectMutation = useCustomMutation();
  const approve = approveMutation.mutate;
  const reject = rejectMutation.mutate;
  const isApproving = approveMutation.mutation.isPending;
  const isRejecting = rejectMutation.mutation.isPending;

  const handleApprove = (item?: ModerationQueueItem) => {
    const targetItem = item || selectedItem;
    if (!targetItem) {return;}

    approve(
      {
        url: `/admin/moderation/${targetItem.id}/approve`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          setSelectedItem(null);
          refetchList();
        },
      }
    );
  };

  const handleReject = (reason: string) => {
    if (!selectedItem) {return;}

    reject(
      {
        url: `/admin/moderation/${selectedItem.id}/reject`,
        method: 'post',
        values: { rejectionReason: reason },
      },
      {
        onSuccess: () => {
          setSelectedItem(null);
          setRejectDialogOpen(false);
          refetchList();
        },
      }
    );
  };

  const handleBulkApprove = (items: ModerationQueueItem[]) => {
    const approveSequentially = async () => {
      for (const item of items) {
        if (item.status === 'PENDING') {
          await new Promise<void>((resolvePromise) => {
            approve(
              {
                url: `/admin/moderation/${item.id}/approve`,
                method: 'post',
                values: {},
              },
              {
                onSuccess: () => resolvePromise(),
                onError: () => resolvePromise(),
              }
            );
          });
        }
      }
      refetchList();
    };
    approveSequentially();
  };

  const handleResetFilters = () => {
    setStatusFilter('all');
    setTypeFilter('all');
    setPage(1);
  };

  const bulkActions: BulkAction<ModerationQueueItem>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          setSelectedItem(items[0]);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Одобрить все',
      icon: <ThumbsUp className="h-4 w-4" />,
      onClick: (items) => handleBulkApprove(items),
      showOnlyWhenSelected: true,
    },
  ];

  const columns: Column<ModerationQueueItem>[] = [
    {
      key: 'id',
      header: 'ID',
      width: '70px',
      sortable: true,
      render: (value) => (
        <span className="font-mono text-xs text-muted-foreground">#{String(value)}</span>
      ),
    },
    {
      key: 'submittedAt',
      header: 'Дата',
      width: '150px',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          {formatDate(String(value))}
        </div>
      ),
    },
    {
      key: 'entityType',
      header: 'Тип',
      width: '130px',
      sortable: true,
      render: (_, item) => (
        <Badge variant="outline" className="gap-1">
          {getTypeIcon(item.entityType)}
          {getTypeLabel(item.entityType)}
        </Badge>
      ),
    },
    {
      key: 'content',
      header: 'Автор',
      width: '200px',
      render: (_, item) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-sm truncate">{getAuthorName(item.content)}</span>
            {item.content?.author?.username && (
              <span className="text-xs text-muted-foreground truncate">
                @{item.content.author.username}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'entityId',
      header: 'Превью',
      render: (_, item) => (
        <div className="max-w-md">
          {item.content?.text ? (
            <div className="text-sm line-clamp-2">
              {item.content.text.replaceAll(/<[^>]*>/g, '').slice(0, 150)}
            </div>
          ) : (
            <span className="text-muted-foreground italic text-sm">Нет текста</span>
          )}
          {item.content?.images && item.content.images.length > 0 && (
            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
              <ImageIcon className="h-3 w-3" />
              {item.content.images.length} изобр.
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      width: '140px',
      sortable: true,
      render: (_, item) => {
        const normalized = normalizeStatus(item.status);
        return (
          <div className="flex items-center gap-2">
            {getStatusIcon(item.status)}
            <Badge variant={STATUS_COLORS[normalized]}>
              {STATUS_LABELS[normalized]}
            </Badge>
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      width: '180px',
      render: (_, item) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setSelectedItem(item)}>
            <Eye className="h-4 w-4" />
          </Button>
          {item.status === 'PENDING' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                onClick={(e) => {
                  e.stopPropagation();
                  handleApprove(item);
                }}
                disabled={isApproving}
              >
                <ThumbsUp className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedItem(item);
                  setRejectDialogOpen(true);
                }}
                disabled={isRejecting}
              >
                <ThumbsDown className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 pt-4">
      {/* Фильтры */}
      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Фильтры
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="start">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="content-status-filter">Статус</Label>
                <Select
                  value={statusFilter}
                  onValueChange={(v) => {
                    setStatusFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="content-status-filter">
                    <SelectValue placeholder="Статус" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все статусы</SelectItem>
                    <SelectItem value="PENDING">На модерации</SelectItem>
                    <SelectItem value="APPROVED">Одобрены</SelectItem>
                    <SelectItem value="REJECTED">Отклонены</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content-type-filter">Тип контента</Label>
                <Select
                  value={typeFilter}
                  onValueChange={(v) => {
                    setTypeFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="content-type-filter">
                    <SelectValue placeholder="Тип" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все типы</SelectItem>
                    <SelectItem value="POST">Посты</SelectItem>
                    <SelectItem value="COMMENT">Комментарии</SelectItem>
                    <SelectItem value="REVIEW">Отзывы</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={handleResetFilters}
                >
                  Сбросить фильтры
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Таблица */}
      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(item) => setSelectedItem(item)}
        emptyMessage="Нет элементов для модерации"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      {/* Панель предпросмотра */}
      <ContentPreviewSheet
        item={selectedItem}
        open={!!selectedItem && !rejectDialogOpen}
        onClose={() => setSelectedItem(null)}
        onApprove={() => handleApprove()}
        onReject={() => setRejectDialogOpen(true)}
        isApproving={isApproving}
        isRejecting={isRejecting}
      />

      {/* Диалог отклонения */}
      <RejectDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleReject}
        isLoading={isRejecting}
      />
    </div>
  );
}
